import { useEffect, useState } from 'react'
import { assetUrl } from '../api'
import type { AdminSport, AdminSportsbook, SportAffiliateLink } from './api'
import {
  fetchSportAffiliateLinks,
  updateAdminSport,
  updateSportAffiliateLinks,
} from './api'
import { SportCarriersPanel } from './CarriedByPanel'
import IntegrationMarketsPanel from './IntegrationMarketsPanel'
import MarketsTradedPanel from './MarketsTradedPanel'
import StateGrid from './StateGrid'
import StringList from './StringList'

interface Props {
  sport: AdminSport
  sportsbooks: AdminSportsbook[]
  canEdit: boolean
  // Markets Traded and Carried By are trading-gated, separate from the
  // admin-gated canEdit that governs the rest of this page.
  canEditMarkets: boolean
  canEditCarriers: boolean
  // Feed-integration market matrix (migration 0069). Gated separately —
  // owner-only until trading approves (INTEGRATIONS_MIN_ROLE in api.ts).
  canViewIntegrations: boolean
  // Write tier for the same panel (higher of view + edit roles).
  canEditIntegrations: boolean
  onSaved: () => void
}

type Mode = 'view' | 'edit'

function linksToMap(rows: SportAffiliateLink[]): Record<number, string> {
  const out: Record<number, string> = {}
  for (const r of rows) out[r.sportsbook_id] = r.url
  return out
}

export default function SportEditor({ sport, sportsbooks, canEdit, canEditMarkets, canEditCarriers, canViewIntegrations, canEditIntegrations, onSaved }: Props) {
  // mode = 'view' is the default. Clicking Edit (only visible to admin/owner)
  // flips to 'edit' — inputs become interactive and Save + Cancel appear.
  // Cancel reverts pending changes by re-seeding state from the source row.
  const [mode, setMode] = useState<Mode>('view')
  const [name, setName] = useState(sport.name)
  const [slug, setSlug] = useState(sport.slug ?? '')
  const [approved, setApproved] = useState<string[]>(
    sport.sport_states.map(s => s.state_code).sort(),
  )
  const [international, setInternational] = useState<string[]>(
    sport.sport_international.map(s => s.region).sort(),
  )
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err', text: string } | null>(null)

  // Carrier ids live in the CarriedByPanel (its own fetch + trading-gated
  // save); the panel reports them up so the AFFILIATE URLS section can
  // render one row per carrier.
  const [carrierIds, setCarrierIds] = useState<number[]>([])
  // Affiliate links keyed by sportsbook_id. Empty string = no URL set.
  const [affiliateLinks, setAffiliateLinks] = useState<Record<number, string>>({})
  const [affiliateBaseline, setAffiliateBaseline] = useState<Record<number, string>>({})

  // Reset whenever the selected sport changes — we don't want to carry
  // unsaved edits across rows.
  useEffect(() => {
    setMode('view')
    setName(sport.name)
    setSlug(sport.slug ?? '')
    setApproved(sport.sport_states.map(s => s.state_code).sort())
    setInternational(sport.sport_international.map(s => s.region).sort())
    setMsg(null)
    setCarrierIds([])
    setAffiliateLinks({})
    setAffiliateBaseline({})
    fetchSportAffiliateLinks(sport.id)
      .then((rows) => {
        const map = linksToMap(rows)
        setAffiliateLinks(map)
        setAffiliateBaseline(map)
      })
      .catch(() => { /* swallow — non-fatal */ })
  }, [sport.id])

  function cancel() {
    setName(sport.name)
    setSlug(sport.slug ?? '')
    setApproved(sport.sport_states.map(s => s.state_code).sort())
    setInternational(sport.sport_international.map(s => s.region).sort())
    setAffiliateLinks(affiliateBaseline)
    setMode('view')
    setMsg(null)
  }

  async function save() {
    setBusy(true)
    setMsg(null)
    try {
      await updateAdminSport(sport.id, {
        name: name.trim(),
        slug: slug.trim() === '' ? null : slug.trim(),
        approved,
        international,
      })
      // Second await — affiliate links. Only includes carriers with a
      // non-empty URL; the RPC treats blanks as deletions. (Carriers
      // themselves save from their own trading-gated panel.)
      const linkPayload: SportAffiliateLink[] = Object.entries(affiliateLinks)
        .filter(([, url]) => url.trim() !== '')
        .map(([id, url]) => ({ sportsbook_id: Number(id), url: url.trim() }))
      await updateSportAffiliateLinks(sport.id, linkPayload)
      setAffiliateBaseline(affiliateLinks)
      setMsg({ kind: 'ok', text: `Saved ${name.trim()}.` })
      setMode('view')
      onSaved()
    }
    catch (e) {
      setMsg({ kind: 'err', text: (e as Error).message })
    }
    finally {
      setBusy(false)
    }
  }

  const readOnly = mode === 'view'

  return (
    <section className={`ad-editor${readOnly ? ' is-read-only' : ''}`}>
      <header className="ad-editor-head">
        <div className="ad-editor-head-stack">
          {sport.logo_url && (
            <div className="ad-editor-logo ad-editor-logo--sport">
              <img src={assetUrl(sport.logo_url)} alt={sport.name} loading="lazy" />
            </div>
          )}
          <h2 className="b-h2">{sport.name}</h2>
        </div>
        <div className="ad-editor-actions">
          {mode === 'view' && canEdit && (
            <button
              type="button"
              onClick={() => { setMode('edit'); setMsg(null) }}
              className="b-btn b-btn-primary"
            >
              EDIT →
            </button>
          )}
          {mode === 'edit' && (
            <>
              <button
                type="button"
                onClick={cancel}
                disabled={busy}
                className="b-btn-ghost"
              >
                CANCEL
              </button>
              <button
                type="button"
                onClick={save}
                disabled={busy}
                className="b-btn b-btn-primary"
              >
                {busy ? 'SAVING…' : 'SAVE →'}
              </button>
            </>
          )}
        </div>
      </header>

      {msg && (
        <p className={`ad-msg b-mono${msg.kind === 'err' ? ' is-err' : ' is-ok'}`}>
          {msg.kind === 'err' ? '⚠' : '✓'} {msg.text}
        </p>
      )}

      <div className="ad-editor-row">
        <label className="ad-field">
          <span className="b-mono-dim b-tiny">NAME</span>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            disabled={readOnly}
            className="ad-input"
          />
        </label>
        <label className="ad-field">
          <span className="b-mono-dim b-tiny">SLUG — leave blank if no widget config exists</span>
          <input
            type="text"
            value={slug}
            onChange={e => setSlug(e.target.value)}
            placeholder="e.g. f1, pfl"
            disabled={readOnly}
            className="ad-input"
          />
        </label>
      </div>
      <SportCarriersPanel
        sportId={sport.id}
        sportsbooks={sportsbooks}
        canEdit={canEditCarriers}
        onCarrierIdsChange={setCarrierIds}
      />


      <MarketsTradedPanel sportId={sport.id} canEdit={canEditMarkets} />

      {canViewIntegrations && <IntegrationMarketsPanel sport={sport} canEdit={canEditIntegrations} />}

      <div className="ad-editor-section">
        <h3 className="b-mono">APPROVED US STATES</h3>
        <StateGrid selected={approved} onChange={setApproved} readOnly={readOnly} />
      </div>

      <div className="ad-editor-section">
        <h3 className="b-mono">INTERNATIONAL</h3>
        <p className="b-mono-dim ad-section-hint">
          Regulated Regions outside the United States, where this sport is explicitly approved for betting.
        </p>
        <StringList
          label="ACTIVE REGIONS"
          values={international}
          onChange={setInternational}
          placeholder="e.g. Ontario, Argentina"
          readOnly={readOnly}
        />
      </div>
      
      <div className="ad-editor-section">
        <h3 className="b-mono">AFFILIATE URLS</h3>
        {carrierIds.length === 0
          ? (
              <p className="b-mono-dim ad-list-empty">
                add a sportsbook above to set a URL
              </p>
            )
          : (
              <div className="ad-affiliate-list">
                {carrierIds.map((sportsbookId) => {
                  const book = sportsbooks.find(s => s.id === sportsbookId)
                  const hasBrand = book?.brand_color != null
                  const hasLogo = book?.logo_url != null
                  return (
                    <label key={sportsbookId} className="ad-affiliate-row">
                      <span className="ad-affiliate-label b-mono">
                        {(hasBrand || hasLogo) && (
                          <span
                            className={`ad-affiliate-logo${book?.logo_fill ? ' is-fill' : ''}`}
                            style={{ background: book?.brand_color ?? 'transparent' }}
                            aria-hidden="true"
                          >
                            {hasLogo
                              ? <img src={assetUrl(book!.logo_url!)} alt="" loading="lazy" />
                              : <span className="ad-affiliate-logo-text">{(book?.name ?? '').slice(0, 2).toUpperCase()}</span>}
                          </span>
                        )}
                        {book?.name ?? `#${sportsbookId}`}
                      </span>
                      <input
                        type="url"
                        value={affiliateLinks[sportsbookId] ?? ''}
                        onChange={e => setAffiliateLinks(prev => ({
                          ...prev,
                          [sportsbookId]: e.target.value,
                        }))}
                        disabled={readOnly}
                        placeholder="https://…"
                        className="ad-input ad-affiliate-input"
                      />
                    </label>
                  )
                })}
              </div>
            )}
      </div>

    </section>
  )
}

